import { Web3Provider } from "@ethersproject/providers";
import { getContract } from "config/contracts";
import { BigNumber, Contract, ethers, PopulatedTransaction } from "ethers";
import ChainFee from "abis/ChainFee.json";
import { NATIVE_TOKEN_DECIMALS } from "config/factors";
import { callContract } from "./callContract";

export const getCalldataBytes = (calldata: string, numBytes: number = 4): string => {
  const PREFIX_0x = 2;
  const sliceEnd = PREFIX_0x + numBytes * 2; // two hexadecimal characters make up 1 byte.
  return calldata.slice(0, sliceEnd);
};

const getChainFeeContract = async (chainId: number, library: Web3Provider) => {
  const chainFeeAddress = getContract(chainId, "ChainFee");
  return new ethers.Contract(chainFeeAddress, ChainFee, library.getSigner());
};

const getTxData = async (
  contract: Contract,
  method: string,
  params: any[]
): Promise<Omit<PopulatedTransaction, "data"> & { data: string }> => {
  const txData = await contract.populateTransaction[method](...params);
  const data = txData?.data ?? contract.interface.encodeFunctionData(method, params);

  return {
    ...txData,
    data,
  };
};

const IsPayableMethod = (contract: Contract, method: string): boolean => {
  const fragment = contract.interface.getFunction(method);
  return fragment.stateMutability.toLowerCase() === "payable";
};

function getUpdatedValue(value: BigNumber | number, fee: BigNumber, decimals = NATIVE_TOKEN_DECIMALS) {
  if (BigNumber.isBigNumber(value)) {
    return value.add(fee);
  }

  if (value !== 0) {
    const feeUnits = ethers.utils.formatUnits(fee, decimals);
    const newValue = +feeUnits + value;
    return ethers.utils.parseUnits(newValue.toString(), decimals);
  }

  return fee;
}

export const withChainFee = async (chainId: number, library: Web3Provider, decimals = NATIVE_TOKEN_DECIMALS) => {
  const chainFeeContract = await getChainFeeContract(chainId, library);
  return async function (...args: Parameters<typeof callContract>) {
    const [chainId, contract, method, params, opts] = args;
    try {
      const txData = await getTxData(contract, method, params);
      const bytes = getCalldataBytes(txData.data);

      const feeAmount = await chainFeeContract.getFeeForContract(txData.to, txData.from, bytes);

      opts.value = opts.value ? getUpdatedValue(opts.value, feeAmount, decimals) : feeAmount; // }

      return await callContract(...args);
    } catch (error) {
      console.error(`withChainFee contract call failed! Contract: ${contract?.address}, method: ${method}`, error);
      throw new Error(`Contract call with chain fee failed: ${error?.message}`);
    }
  };
};
